import {
  availabilityReasonField,
  endField,
  isOpenEndedField,
  notesField,
  repeatEndInclusiveField,
  startField,
  startImmediatelyField,
} from 'pages/Availability/common/FormDrawer/sharedValidationFields';
import {
  AssetInputErrors,
  AvailabilityFormDrawerEnabledFields,
  PowerEnergyInput,
} from 'pages/Availability/common/FormDrawer/types';
import { array, lazy, object, string } from 'yup';

export type AssetOperationalLimits = Record<
  string,
  { maxExportPower: number; maxImportPower: number; energyCapacity: number }
>;

/**
 * Type for nested asset input errors as returned by Formik
 */
export type AssetInputsErrors = Record<string, AssetInputErrors>;

/**
 * Helper to check if a value is filled (not null, undefined, or empty string)
 */
const isFilled = (value: string | null | undefined): boolean =>
  value !== null && value !== undefined && value !== '';

/**
 * Creates a validation schema for a single asset's power/energy inputs
 */
const createAssetInputSchema = (maxLimits?: {
  maxExportPower: number;
  maxImportPower: number;
  energyCapacity: number;
}) =>
  object()
    .shape({
      maxExportPower: string()
        .nullable()
        .test(
          'is-valid-number',
          'Export limit must be a valid number.',
          (value) => {
            if (!isFilled(value)) return true;
            return !Number.isNaN(Number(value));
          },
        )
        .test(
          'min-value',
          'Export limit must be greater than or equal to zero.',
          (value) => {
            if (!isFilled(value)) return true;
            return Number(value) >= 0;
          },
        )
        .test(
          'max-value',
          `Export limit must be less than or equal to ${maxLimits?.maxExportPower ?? 0} MW.`,
          (value) => {
            if (!isFilled(value) || !maxLimits) return true;
            return Number(value) <= maxLimits.maxExportPower;
          },
        ),
      maxImportPower: string()
        .nullable()
        .test(
          'is-valid-number',
          'Import limit must be a valid number.',
          (value) => {
            if (!isFilled(value)) return true;
            return !Number.isNaN(Number(value));
          },
        )
        .test(
          'min-value',
          'Import limit must be greater than or equal to zero.',
          (value) => {
            if (!isFilled(value)) return true;
            return Number(value) >= 0;
          },
        )
        .test(
          'max-value',
          `Import limit must be less than or equal to ${maxLimits?.maxImportPower ?? 0} MW.`,
          (value) => {
            if (!isFilled(value) || !maxLimits) return true;
            return Number(value) <= maxLimits.maxImportPower;
          },
        ),
      energyCapacity: string()
        .nullable()
        .required('Please enter an energy limit for the reduction.')
        .test(
          'is-valid-number',
          'Energy limit must be a valid number.',
          (value) => {
            if (!isFilled(value)) return true;
            return !Number.isNaN(Number(value));
          },
        )
        .test(
          'min-value',
          'Energy limit must be greater than or equal to zero.',
          (value) => {
            if (!isFilled(value)) return true;
            return Number(value) >= 0;
          },
        )
        .test(
          'max-value',
          `Energy limit must be less than or equal to ${maxLimits?.energyCapacity ?? 0} MWh.`,
          (value) => {
            if (!isFilled(value) || !maxLimits) return true;
            return Number(value) <= maxLimits.energyCapacity;
          },
        ),
    })
    .test('at-least-one-power-limit', function (values) {
      if (!values) return false;
      const hasExport = isFilled(values.maxExportPower);
      const hasImport = isFilled(values.maxImportPower);
      if (hasExport || hasImport) return true;

      // Create error at the 'limits' path within this asset's object
      return this.createError({
        path: `${this.path}.limits`,
        message: 'At least one of Export or Import limit is required.',
      });
    });

export const assetLevelFormValidationSchema = (
  enabledFields: AvailabilityFormDrawerEnabledFields,
  assetOperationalLimits: AssetOperationalLimits = {},
) =>
  object().shape({
    start: startField(enabledFields),
    end: endField(),
    startImmediately: startImmediatelyField(),
    isOpenEnded: isOpenEndedField(),
    availabilityReason: availabilityReasonField(),
    notes: notesField(),
    selectedAssets: array()
      .of(string())
      .min(1, 'Please select at least one asset.')
      .required('Please select at least one asset.'),
    repeatEndInclusive: repeatEndInclusiveField(),
    assetInputs: lazy((value: Record<string, PowerEnergyInput> | undefined) => {
      if (!value || typeof value !== 'object') {
        return object().nullable();
      }
      const shape: Record<
        string,
        ReturnType<typeof createAssetInputSchema>
      > = {};
      for (const assetKey of Object.keys(value)) {
        shape[assetKey] = createAssetInputSchema(
          assetOperationalLimits[assetKey],
        );
      }
      return object().shape(shape);
    }),
  });
